import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';

// Third-party imports
import { useDispatch, useSelector } from 'react-redux';
import {
    doc,
    getDoc,
    setDoc,
    serverTimestamp,
    collection,
    query,
    onSnapshot,
    orderBy,
    where,
    getDocs,
    Timestamp,
    limit,
} from 'firebase/firestore';
import {
    NOTIFICATION_MAX_GEOFENCE_IN_UI,
    NOTIFICATION_MAX_OFFLINE_IN_UI,
    NOTIFICATION_FIRESTORE_QUERY_LIMIT,
    NOTIFICATION_OFFLINE_FIRESTORE_QUERY_LIMIT,
    getNotificationLookbackDate,
    isGeofenceCloudNotification,
    isSosCloudNotification,
    isOfflineCloudNotification,
    mapNotificationDocSnap,
    buildNotificationListFromQueries,
    ENABLE_CLIENT_GEOFENCE_NOTIFICATIONS,
} from './constants/notificationConfig';
import { normalizeDevicesForRedux } from './utils/firestoreTimestamps';
import { onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';

// Redux store and slices
import { store } from './store';
import {
    fetchAllDevices,
    setSelectedDevice,
    clearSelectedDevice,
    setFilteredDevices,
    updateDevicesRealtime,
    setRealtimeError,
    setRealtimeActive,
    setupRealtimeDeviceListener,
    cleanupRealtimeListener,
    selectFilteredDevices // Import the new selector
} from './store/slices/devicesSlice';
import {
    setActiveFilters,
    removeFilter,
    toggleNameplates,
    toggleGeofences,
    setFilterOptions
} from './store/slices/filtersSlice';
import {
    toggleSidebar,
    addAlert,
    dismissAlert,
    dismissAllAlerts,
    clearDismissedSosDevice,
    setSelectedBeachGeofence,
    clearSelectedBeachGeofence,
    setShouldNavigateToSelectedDevice,
    setSelectedCity,
    setSelectedState,
    setSelectedCountry
} from './store/slices/uiSlice';
import {
    fetchTowerLocations,
    fetchBeachLocations,
    setBeachLocations,
    fetchOfficeLocations,
    fetchTritonLocations,
    fetchAurusLocations,
    fetchSeaHorseLocations,
    fetchAdafruitLocations,
    setAdafruitLocations,
    mapAdafruitSnapshotToDevices,
    canViewAdafruitLocations
} from './store/slices/locationsSlice';
import { updateNotifications, markNotificationsAsRead, clearAllNotifications, setUserNotificationState, loadUserNotificationState, removeNotification,
    addNotification
} from './store/slices/notificationsSlice';
import {
    setAuthReady,
    setUserProfile,
    setUserRoleAndPermissions,
    clearAuthError,
    onAuthStateChanged as onAuthStateChangedAction,
    signInWithGoogle,
    signOut,
    verifyOTP,
    restoreSession,
    setOTPVerificationRequired,
    setOTPVerificationCompleted,
    clearOTPError
} from './store/slices/authSlice';
import { openAdminPanel, closeAdminPanel } from './store/slices/adminSlice';
import { clearHistory, startLiveSession, appendLivePoint } from './store/slices/deviceHistorySlice';

// Components
import MapComponent from './components/MapComponent';
import DeviceList from './components/DeviceList';
import FilterDropdown from './components/FilterDropdown';
import ActiveFilters from './components/ActiveFilters';
import Alerts from './components/Alerts';
import GoogleLogin from './components/GoogleLogin';
import Notifications from './components/Notifications';
import BeachInfoPanel from './components/BeachInfoPanel';
import AdminCommandPanel from './components/AdminCommandPanel';
import OTPVerification from './components/OTPVerification';
import TempAccessHandler from './components/TempAccessHandler';
import MainLayout from './components/layout/MainLayout';
// DataFetcher removed — AppRedux.js handles all data fetching directly
// Import the new component
// import ReduxVerification from './components/ReduxVerification';
// import ReduxStateInspector from './components/ReduxStateInspector';

// Services
import geofencingNotificationService from './services/geofencingNotificationService';
import { canViewBeachGeofences, mapGeofenceDocToBeach } from './utils/geofencingUtils';
import { hasNotificationPermission as checkNotificationPermission } from './utils/notificationPermissions';
import simplifiedAuthService from './services/simplifiedAuthService';
import sessionManager from './services/sessionManager';

// Utils
import { checkAdminAccess } from './utils/adminUtils';

// Config
import { db, auth } from './firebase-config';
import config from './config';
import { getLgBandDisplayId, buildSosAlertMessage, findSosLgBandDevices, toLgBandMapDevice, sosNotificationIdFor, isNewSosPacket, getSosGeofenceStatus, getLgBandLatestPacketTime, getSosAnchorLatLng, findNearestTower, findNearestGeofence, getLgBandLatestPacketMs, isRepeatedSosPress, getSosRaisedAtIso } from './utils/lgBandSos';
import { playSosTone } from './utils/sosAudio';
import { openSosIncident, closeSosIncident, createSosNotification, loadSosTowers } from './services/sosIncidentService';

/** Toast when user selects an LG Band / Adafruit device for live map tracking. */
function alertLgBandLiveTracking(dispatch, device) {
    if (!device?.device_type) return;
    const t = String(device.device_type).toLowerCase();
    if (t !== 'lg_band' && t !== 'adafruit') return;
    const name = device.displayName || device.name || device.device_id || 'LG Band';
    dispatch(addAlert({
        message: `Live Tracking User: ${name}`,
        type: 'live_tracking',
        deviceStatus: device.status,
    }));
}

function getDeviceLatLngForLiveSession(device) {
    if (!device) return null;
    let lat = device.last_latitude ?? device.latitude ?? device.location?.latitude ?? device.coordinates?.lat;
    let lng = device.last_longitude ?? device.longitude ?? device.location?.longitude ?? device.coordinates?.lng;
    if (typeof lat === 'string') lat = parseFloat(lat);
    if (typeof lng === 'string') lng = parseFloat(lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (lat === 0 && lng === 0) return null;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
    return { lat, lng };
}

function getReadingTimeMs(reading, docId) {
    const ts = reading?.timestamp;
    if (ts && typeof ts.toDate === 'function') {
        const t = ts.toDate().getTime();
        if (!Number.isNaN(t)) return t;
    }
    if (ts instanceof Date) return ts.getTime();
    if (typeof ts === 'string') {
        const t = new Date(ts).getTime();
        if (!Number.isNaN(t)) return t;
    }
    const m = String(docId || '').match(/^(\d{4})(\d{2})(\d{2})_/);
    if (m) {
        return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0).getTime();
    }
    return null;
}

function getReadingLatLng(reading) {
    const loc = reading?.location;
    if (loc != null) {
        if (typeof loc.latitude === 'number' && typeof loc.longitude === 'number') {
            if (!(loc.latitude === 0 && loc.longitude === 0)) return { lat: loc.latitude, lng: loc.longitude };
        }
        if (typeof loc.lat === 'number' && typeof loc.lng === 'number') {
            if (!(loc.lat === 0 && loc.lng === 0)) return { lat: loc.lat, lng: loc.lng };
        }
    }
    const rc = reading?.received_coordinates;
    if (rc?.lat != null && rc?.lng != null) {
        const lat = typeof rc.lat === 'number' ? rc.lat : parseFloat(rc.lat);
        const lng = typeof rc.lng === 'number' ? rc.lng : parseFloat(rc.lng);
        if (Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0)) {
            return { lat, lng };
        }
    }
    return null;
}

function startLgLiveTrackingSession(dispatch, device) {
    const deviceId = device?.device_id || device?.id;
    if (!deviceId) {
        dispatch(clearHistory());
        return;
    }
    const t = String(device?.device_type || '').toLowerCase();
    if (t !== 'lg_band' && t !== 'adafruit') {
        dispatch(clearHistory());
        return;
    }
    const startedAtMs = Date.now();
    const ll = getDeviceLatLngForLiveSession(device);
    const docId = device?.adafruit_firestore_doc_id != null ? String(device.adafruit_firestore_doc_id) : null;
    dispatch(startLiveSession({
        deviceId,
        adafruitFirestoreDocId: docId,
        startedAtMs,
        initialPoint: ll ? {
            lat: ll.lat,
            lng: ll.lng,
            timestamp: startedAtMs,
            status: device?.status || null,
        } : null,
    }));
}

// Development utilities - Load notification test utility
if (process.env.NODE_ENV === 'development') {
    import('./utils/notificationStorageTest').then(() => {
        console.log('🔧 Notification test utility loaded! Type "notificationStorageTest.help()" in console.');
    }).catch(() => { // Silently fail if utility not found
    });
}

console.error("************************************************************");
console.error("**** YOU ARE RUNNING THE LATEST VERSION OF AppRedux.js! ****");
console.error("************************************************************");

const authFlowInitialState = {
    step: 'idle', // idle, awaiting_otp, generating_otp, loading_permissions, authorized
};

function authFlowReducer(state, action) {
    switch (action.type) {
        case 'AUTHENTICATED_AWAITING_OTP':
            return {
                ...state,
                step: 'awaiting_otp'
            };
        case 'GENERATE_OTP':
            return {
                ...state,
                step: 'generating_otp'
            };
        case 'OTP_GENERATION_COMPLETE':
            return {
                ...state,
                step: 'idle'
            }; // Go to idle to wait for user input
        case 'OTP_VERIFIED_LOADING_PERMISSIONS':
            return {
                ...state,
                step: 'loading_permissions'
            };
        case 'AUTHORIZED':
            return {
                ...state,
                step: 'authorized'
            };
        case 'RESET':
            return authFlowInitialState;
        default:
            return state;
    }
}

function AppContentRedux() {
    const dispatch = useDispatch();
    const [authFlowState, authFlowDispatch] = React.useReducer(authFlowReducer, authFlowInitialState);

    const {
        isAuthenticated,
        isAuthReady,
        isAuthorized,
        user,
        permissions,
        isProfileLoaded,
        organization, // Correctly select organization from the auth state
        role, // Add role to the selector
        sessionId,
        sessionExpiresAt,
        lastActivity,
        isSessionRestored,
        error: authError, // <-- Add this
        isLoading: authLoading,
        authLoadingPhase,
        // OTP verification state
        otpVerificationRequired,
        otpVerificationCompleted,
        otpError
    } = useSelector(state => state.auth);

    const { isAdminPanelOpen } = useSelector(state => state.admin);

    // Local state for mobile detection and admin access
    const [isMobile, setIsMobile] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    // City/State boundary selection - now using Redux
    const selectedCity = useSelector(state => state.ui.selectedCity);
    const selectedState = useSelector(state => state.ui.selectedState);
    const selectedCountry = useSelector(state => state.ui.selectedCountry || 'in');
    const [isBoundaryLoading, setIsBoundaryLoading] = useState(false);

    // The state machine logic is now handled by the authFlowReducer
    // const isGeneratingOTPRef = useRef(false);
    // const [otpGeneratedThisSession, setOtpGeneratedThisSession] = useState(false);


    // Redux selectors
    const {
        allDevices,
        selectedDevice,
        lastUpdateTime,
        isBackendOnline,
        // loading: devicesLoading, // Unused for now
        // error: devicesError, // Unused for now
    } = useSelector(state => state.devices);

    // Use the memoized selector for filtered devices
    const filteredDevices = useSelector(selectFilteredDevices);

    const { activeFilters, showNameplates, showGeofences, filterOptions } = useSelector(state => state.filters);

    const { isSidebarOpen, alerts, dismissedSosDeviceIds, selectedBeachGeofence, shouldNavigateToSelectedDevice } = useSelector(state => state.ui);

    const {
        towerLocations,
        beachLocations,
        officeLocations,
        tritonLocations,
        aurusLocations,
        seaHorseLocations,
        adafruitLocations
    } = useSelector(state => state.locations);

    const {
        notifications,
        // unreadCount, // Unused for now
        // loading: notificationsLoading, // Unused for now
    } = useSelector(state => state.notifications);
    const {
        liveSessionActive,
        liveSessionDeviceId,
        liveSessionDocId,
        liveSessionStartedAtMs,
    } = useSelector(state => state.deviceHistory);

    // Minute tick so the "SOS today only" rule re-evaluates at IST midnight without waiting for a packet.
    const [sosClockTick, setSosClockTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setSosClockTick((t) => t + 1), 60 * 1000);
        return () => clearInterval(id);
    }, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const sosLgBandDevices = useMemo(() => findSosLgBandDevices(adafruitLocations), [adafruitLocations, sosClockTick]);
    const announcedSosRef = useRef(new Map());   // device_id -> packet ms of the SOS press last announced
    // Atlas towers list for the SOS "Nearest Tower" field (towerLocations is empty for roles without viewTowers).
    const [sosTowers, setSosTowers] = useState([]);
    useEffect(() => {
        if (!isAuthorized || !organization?.id) return undefined;
        let cancelled = false;
        loadSosTowers(organization.id).then((rows) => { if (!cancelled) setSosTowers(rows); });
        return () => { cancelled = true; };
    }, [isAuthorized, organization?.id]);
    const sosTowerList = (towerLocations && towerLocations.length > 0) ? towerLocations : sosTowers;
    const locateSosDeviceRef = useRef(null);     // set below once locateSosDevice is defined

    useEffect(() => {
        const sosDevices = sosLgBandDevices;
        const activeSosIds = new Set(sosDevices.map((d) => d.device_id).filter(Boolean));

        (dismissedSosDeviceIds || []).forEach((deviceId) => {
            if (!activeSosIds.has(deviceId)) {
                dispatch(clearDismissedSosDevice(deviceId));
            }
        });
        // A band that left SOS may announce again on its next press.
        announcedSosRef.current.forEach((_ms, id) => { if (!activeSosIds.has(id)) announcedSosRef.current.delete(id); });

        // New SOS press (first of an episode, or a NEWER packet than the press last announced here):
        // persist/re-stamp the shared incident, bell entry + tone.
        sosDevices.forEach((d) => {
            if (!d.device_id) return;
            const packetMs = getLgBandLatestPacketMs(d);
            const announcedMs = announcedSosRef.current.get(d.device_id);
            if (announcedMs != null && !(Number.isFinite(packetMs) && packetMs > announcedMs)) return;
            announcedSosRef.current.set(d.device_id, Number.isFinite(packetMs) ? packetMs : 0);
            const repress = isRepeatedSosPress(d);
            // A repeated press re-anchors the SOS at the band's position for THAT press, not the old one.
            const liveDevice = repress ? { ...d, sos_incident: null } : d;
            const geo = getSosGeofenceStatus(liveDevice, beachLocations);
            const outsideGeofence = geo.hasPosition && !geo.inside;
            const sosAnchor = getSosAnchorLatLng(liveDevice);
            const sosNearest = sosAnchor ? findNearestTower(sosAnchor.lat, sosAnchor.lng, sosTowerList) : null;
            const sosNearestBeach = (outsideGeofence && sosAnchor) ? findNearestGeofence(sosAnchor.lat, sosAnchor.lng, beachLocations) : null;
            if (isNewSosPacket(d) || repress) {
                openSosIncident(d.adafruit_firestore_doc_id, getLgBandLatestPacketTime(d), {
                    outside_geofence: outsideGeofence,
                    geofence_name: geo.geofenceName,
                    nearest_beach: sosNearestBeach?.name || null,
                    device_id: d.deviceId || null,
                    // position at the moment of the SOS: the map anchors the SOS here from now on
                    lat: geo.hasPosition ? Number(d.last_latitude) : null,
                    lng: geo.hasPosition ? Number(d.last_longitude) : null,
                }).catch((e) => console.error('🆘 openSosIncident failed:', e));
            }
            const sosNotification = {
                id: sosNotificationIdFor(d),
                eventType: 'sos',
                category: 'lg_band_sos',
                deviceId: d.device_id,
                deviceType: 'lg_band',
                deviceName: d.displayName || d.name,
                lgBandId: getLgBandDisplayId(d),
                outsideGeofence,
                geofenceName: geo.geofenceName,
                nearestTower: sosNearest?.name || null,
                nearestTowerDistanceM: sosNearest?.distanceM ?? null,
                nearestBeach: sosNearestBeach?.name || null,
                nearestBeachDistanceM: sosNearestBeach?.distanceM ?? null,
                timestamp: d.latest_timestamp_iso || new Date().toISOString(),
            };
            dispatch(addNotification(sosNotification));
            // Same path as geofence entry/exit: one shared document in `notifications` (org feed).
            if ((isNewSosPacket(d) || repress) && organization?.id) {
                const { id: notifId, timestamp: sosAt, ...notifFields } = sosNotification;
                createSosNotification(notifId, organization.id, { ...notifFields, sosAt });
            }
            playSosTone();
        });

        const existingSosAlert = alerts.find((a) => a.type === 'sos_emergency');

        if (sosDevices.length === 0) {
            if (existingSosAlert) {
                dispatch(dismissAlert(existingSosAlert.id));
            }
            return;
        }

        const preferred =
            sosDevices.find((d) => d.device_id === selectedDevice?.device_id)
            || sosDevices[0];
        const deviceId = preferred.device_id;

        if ((dismissedSosDeviceIds || []).includes(deviceId)) {
            if (existingSosAlert) {
                dispatch(dismissAlert(existingSosAlert.id));
            }
            return;
        }

        const lgBandId = getLgBandDisplayId(preferred);
        const message = buildSosAlertMessage(preferred);
        const geo = getSosGeofenceStatus(preferred, beachLocations);
        const outsideGeofence = (preferred.sos_incident?.outside_geofence === true) || (geo.hasPosition && !geo.inside);
        const raisedAt = getSosRaisedAtIso(preferred);

        if (
            existingSosAlert
            && existingSosAlert.deviceId === deviceId
            && existingSosAlert.lgBandId === lgBandId
            && existingSosAlert.outsideGeofence === outsideGeofence
            && existingSosAlert.sosAt === raisedAt
            && existingSosAlert.nearestTower === (getSosAnchorLatLng(preferred) ? (findNearestTower(getSosAnchorLatLng(preferred).lat, getSosAnchorLatLng(preferred).lng, sosTowerList)?.name || null) : null)
        ) {
            return;
        }

        const anchor = getSosAnchorLatLng(preferred);
        const nearest = anchor ? findNearestTower(anchor.lat, anchor.lng, sosTowerList) : null;
        const nearestBeach = (outsideGeofence && anchor) ? findNearestGeofence(anchor.lat, anchor.lng, beachLocations) : null;
        dispatch(addAlert({
            type: 'sos_emergency',
            message,
            deviceId,
            lgBandId,
            firestoreDocId: preferred.adafruit_firestore_doc_id,
            outsideGeofence,
            hasPosition: geo.hasPosition,
            geofenceName: geo.geofenceName,
            sosAt: raisedAt,
            nearestTower: nearest?.name || null,
            nearestTowerDistanceM: nearest?.distanceM ?? null,
            nearestBeach: nearestBeach?.name || null,
            nearestBeachDistanceM: nearestBeach?.distanceM ?? null,
        }));
    }, [sosLgBandDevices, alerts, dismissedSosDeviceIds, selectedDevice, beachLocations, sosTowerList, organization?.id, dispatch]);

    // Mobile detection effect
    useEffect(() => {
        const checkMobile = () => {
            const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 1024;
            setIsMobile(isMobileDevice);
        };

        // Check on mount
        checkMobile();

        // Check on resize
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Session restoration effect - check localStorage first
    useEffect(() => {
        const attemptSessionRestoration = async () => {
            console.log('🔄 Attempting session restoration...');

            try {
                const result = await dispatch(restoreSession()).unwrap();

                if (result) {
                    console.log('✅ Session restored successfully, skipping OTP flow');

                    // Start activity tracking for restored session
                    sessionManager.initActivityTracking();

                    // Set auth ready since we have a valid session
                    dispatch(setAuthReady());
                } else {
                    console.log('❌ Session restoration returned no result');
                    // Set auth ready to continue with normal flow
                    dispatch(setAuthReady());
                }
            } catch (error) {
                console.log('❌ Session restoration failed:', error);
                // Set auth ready to continue with normal flow
                dispatch(setAuthReady());
            }
        };

        // Always attempt restoration on mount if not already restored
        if (!isSessionRestored) {
            attemptSessionRestoration();
        } else if (!isAuthReady) { // If session is already restored but auth is not ready, set it ready
            dispatch(setAuthReady());
        }
    }, [dispatch, isSessionRestored, isAuthReady]);

    // The onAuthStateChanged listener has been removed to simplify the auth flow.
    // The logic is now handled by restoreSession and handleRedirectResult.

    // Handle session expiry events
    useEffect(() => {
        const handleSessionExpired = () => {
            console.log('⏰ Session expired, logging out user');
            dispatch(signOut());
        };

        // Listen for session expiry events from SessionManager
        window.addEventListener('sessionExpired', handleSessionExpired);

        return () => {
            window.removeEventListener('sessionExpired', handleSessionExpired);
        };
    }, [dispatch]);

    // Handle redirect result after Google sign-in
    useEffect(() => {
        const handleRedirectResult = async () => {
            try {
                const result = await simplifiedAuthService.handleRedirectResult();
                if (result.success && result.user) {
                    console.log('✅ Redirect sign-in successful:', result.user.email);
                    // After a successful sign-in, the existing useEffects that depend on
                    // `isAuthenticated` will trigger the OTP flow correctly.
                    // We just need to update the state.
                    dispatch(signInWithGoogle.fulfilled(result));
                }
            } catch (error) {
                console.error('❌ Error handling redirect result:', error);
            }
        };

        // Only run this once when component mounts
        handleRedirectResult();
    }, [dispatch]);

    // This effect determines which step of the auth flow we are in.
    useEffect(() => {
        if (isSessionRestored || (isAuthenticated && isAuthorized && isProfileLoaded)) {
            authFlowDispatch({ type: 'AUTHORIZED' });
        } else if (isAuthenticated && user && !isProfileLoaded && !otpVerificationRequired) {
            authFlowDispatch({ type: 'AUTHENTICATED_AWAITING_OTP' });
        } else if (!isAuthenticated) {
            authFlowDispatch({ type: 'RESET' });
        }
    }, [
        isAuthenticated,
        isAuthorized,
        isProfileLoaded,
        isSessionRestored,
        user,
        otpVerificationRequired
    ]);

    // This effect executes the logic for the current auth flow step.
    useEffect(() => {
        // The logic for generating OTP has been moved to the OTPLogin component
        // and is handled by the requestOtp thunk. This effect is no longer needed.
    }, [authFlowState.step, user, dispatch]);
    // Removed dependency on otpGeneratedThisSession

    // The logic for loading permissions is now handled within the verifyOTP thunk.
    // This useEffect is no longer needed.

    // useEffect hook to update filter options based on permissions and data.
    useEffect(() => {
        if (!isProfileLoaded || !permissions)
            return;



        const buildFilterOptions = () => {
            console.log('🔧 Updating filter options based on user permissions:', permissions);
            const allowedOptions = [{
                value: 'all',
                label: 'Total Fleet'
            }];
            const vehicleTypes = new Set();

            allDevices.forEach(device => {
                if (device.device_type && device.device_type !== 'tower' && device.device_type !== 'office') {
                    vehicleTypes.add(device.device_type);
                }
            });
            if (tritonLocations?.length)
                vehicleTypes.add('triton');



            if (aurusLocations?.length)
                vehicleTypes.add('aurus');



            if (seaHorseLocations?.length)
                vehicleTypes.add('seahorse');



            vehicleTypes.add('bike');
            vehicleTypes.add('boat');
            vehicleTypes.add('truck');

            const availableVehicleTypes = Array.from(vehicleTypes);
            const vehicleTypeLabels = {
                mobile: 'Mobile',
                aurus: 'Aurus',
                triton: 'Triton',
                vehicle: 'Vehicle',
                ebike: 'Ebike',
                boat: 'Boat',
                jeep: 'Cars and Jeep',
                bike: 'Bike',
                truck: 'Truck',
                atv: 'ATV',
                seahorse: 'Sea Horse',
                swimzone: 'Swimzones'
            };

            if (permissions.viewVehicles === true || permissions.viewAurus === true) {
                availableVehicleTypes.forEach(type => {
                    if (vehicleTypeLabels[type]) {
                        allowedOptions.push({ value: type, label: vehicleTypeLabels[type] });
                    }
                });
            } else if ((Array.isArray(permissions.viewVehicles) && permissions.viewVehicles.length > 0) || permissions.viewAurus === true) {
                const allowedVehicleTypes = new Set();
                if (permissions.viewAurus === true && availableVehicleTypes.includes('aurus')) {
                    allowedVehicleTypes.add('aurus');
                }
                if (Array.isArray(permissions.viewVehicles)) {
                    permissions.viewVehicles.forEach(permission => {
                        if (['office_vehicle', 'operation_vehicle', 'store_vehicles'].includes(permission)) {
                            [
                                'jeep',
                                'bike',
                                'boat',
                                'truck',
                                'atv'
                            ].forEach(type => {
                                if (availableVehicleTypes.includes(type))
                                    allowedVehicleTypes.add(type);



                            });
                        }
                        if (vehicleTypeLabels[permission] && availableVehicleTypes.includes(permission)) {
                            allowedVehicleTypes.add(permission);
                        }
                        if (permission === 'vehicle') {
                            [
                                'jeep',
                                'atv',
                                'boat',
                                'bike',
                                'truck'
                            ].forEach(type => {
                                if (availableVehicleTypes.includes(type))
                                    allowedVehicleTypes.add(type);



                            });
                        }
                        if (permission === 'jeep' && availableVehicleTypes.includes('atv')) {
                            allowedVehicleTypes.add('atv');
                        }
                    });
                }
                Array.from(allowedVehicleTypes).forEach(type => {
                    if (vehicleTypeLabels[type]) {
                        allowedOptions.push({ value: type, label: vehicleTypeLabels[type] });
                    }
                });
            }

            if (permissions.viewTowers) {
                allowedOptions.push({ value: 'towers', label: 'Towers' });
            }

            const canViewLGBands = canViewAdafruitLocations(permissions);
            if (canViewLGBands && !allowedOptions.some((o) => o.value === 'lg_band')) {
                allowedOptions.push({ value: 'lg_band', label: 'LG Bands' });
            }

            // Always add swimzones to filter options (assuming users can view swimzones)
            allowedOptions.push({ value: 'swimzone', label: 'Swimzones' });

            if (Array.isArray(permissions.viewGeofences) && permissions.viewGeofences.includes('beach')) {
                allowedOptions.push({ value: 'beaches', label: 'Beaches' });
            } else if (permissions.viewGeofences === true) {
                allowedOptions.push({ value: 'beaches', label: 'Beaches' });
            }

            return allowedOptions;
        };

        const options = buildFilterOptions();
        dispatch(setFilterOptions(options));

    }, [
        dispatch,
        isProfileLoaded,
        permissions,
        allDevices,
        tritonLocations,
        aurusLocations,
        seaHorseLocations
    ]);

    // Debug: Monitor auth state to help with admin check debugging
    useEffect(() => {
        if (isAuthenticated && auth) {
            console.log('🔍 AUTH STATE MONITOR:', {
                authExists: !!auth,
                currentUser: !!auth.currentUser,
                currentUserUid: auth.currentUser?.uid,
                currentUserEmail: auth.currentUser?.email,
                userProp: user,
                userPropType: typeof user,
                isProfileLoaded,
                isAuthorized
            });
        }
    }, [isAuthenticated, auth, user, isProfileLoaded, isAuthorized]);


    // Check admin access when user is authenticated and profile is loaded
    useEffect(() => {
        // Always log when this effect runs
        console.log('🔍 ADMIN CHECK useEffect triggered:', {
            isAuthenticated,
            isProfileLoaded,
            isAuthorized,
            hasUser: !!user,
            userType: typeof user,
            userIsString: typeof user === 'string',
            hasUid: !!user?.uid,
            hasEmail: !!user?.email,
            userEmail: typeof user === 'string' ? user : user?.email,
            userUid: user?.uid,
            fullUser: user,
            authExists: !!auth,
            currentUserUid: auth?.currentUser?.uid,  // May be null with OTP auth
            currentUserEmail: auth?.currentUser?.email  // May be null with OTP auth
        });

        // Wait until user is authenticated and authorized
        if (!isAuthenticated) {
            console.log('🔍 ADMIN CHECK: Early return - not authenticated');
            setIsAdmin(false);
            return;
        }

        if (!isAuthorized) {
            console.log('🔍 ADMIN CHECK: Early return - not authorized');
            setIsAdmin(false);
            return;
        }

        if (!isProfileLoaded) {
            console.log('🔍 ADMIN CHECK: Waiting for profile to load...');
            return;
        }

        // Handle case where user might be a string (email) or object
        // NOTE: With OTP-based auth, auth.currentUser may be null, so we rely on Redux user state
        let userUid = null;
        let userEmail = null;

        // First priority: Check the user prop from Redux (primary source for OTP auth)
        if (typeof user === 'string') {
            // User is just an email string - try to get UID from sessionManager
            userEmail = user;
            console.log('🔍 ADMIN CHECK: User prop is a string (email):', user);
            
            // Try to get UID from sessionManager's stored userData
            try {
                const storedSession = sessionManager.getStoredSession();
                if (storedSession?.userData) {
                    const userData = storedSession.userData;
                    if (userData.uid) {
                        userUid = userData.uid;
                        userEmail = userData.email || userEmail;
                        console.log('🔍 ADMIN CHECK: Got UID from sessionManager:', { userUid, userEmail });
                    }
                }
            } catch (error) {
                console.log('🔍 ADMIN CHECK: Could not get userData from sessionManager:', error);
            }
            
            // Fallback: Try auth.currentUser (may be null with OTP auth)
            if (!userUid && auth && auth.currentUser) {
                userUid = auth.currentUser.uid;
                userEmail = auth.currentUser.email || userEmail;
                console.log('🔍 ADMIN CHECK: Got UID from auth.currentUser (fallback):', { userUid, userEmail });
            }
        } else if (user && typeof user === 'object') {
            // User is an object - extract uid and email
            userUid = user.uid;
            userEmail = user.email;
            console.log('🔍 ADMIN CHECK: User prop is an object:', { 
                propUid: user.uid, 
                propEmail: user.email,
                fullUserObject: user
            });
            
            // If object doesn't have uid, try sessionManager
            if (!userUid) {
                try {
                    const storedSession = sessionManager.getStoredSession();
                    if (storedSession?.userData?.uid) {
                        userUid = storedSession.userData.uid;
                        console.log('🔍 ADMIN CHECK: Got UID from sessionManager (user object had no uid):', userUid);
                    }
                } catch (error) {
                    console.log('🔍 ADMIN CHECK: Could not get UID from sessionManager:', error);
                }
            }
        }

        // Final fallback: Try auth.currentUser (unlikely to work with OTP auth, but worth trying)
        if (!userUid && auth && auth.currentUser) {
            userUid = auth.currentUser.uid;
            userEmail = auth.currentUser.email || userEmail;
            console.log('🔍 ADMIN CHECK: Got UID from Firebase Auth (final fallback):', { userUid, userEmail });
        }

        if (!userUid) {
            console.log('🔍 ADMIN CHECK: Early return - no user UID available after all attempts');
            console.log('🔍 ADMIN CHECK: Debug info:', {
                user,
                userType: typeof user,
                auth: !!auth,
                currentUser: !!auth?.currentUser,
                currentUserUid: auth?.currentUser?.uid,
                sessionManagerAvailable: !!sessionManager,
                sessionData: sessionManager?.getStoredSession?.()
            });
            setIsAdmin(false);
            return;
        }

        if (!userEmail) {
            console.log('🔐 ADMIN CHECK: Waiting for user email...', {
                userUid,
                userEmail
            });
            return;
        }

        console.log('🔐 All prerequisites met, starting admin check...', {
            userUid,
            userEmail
        });

        const checkAdmin = async () => {
            try {
                console.log('🔐 Starting admin access check for user:', {
                    uid: userUid,
                    email: userEmail
                });
                
                const result = await checkAdminAccess(userUid, userEmail);
                
                console.log('🔐 Admin access check completed:', result);
                console.log('🔐 Admin access check result:', { 
                    hasAdminAccess: result.isAdmin, 
                    userEmail: userEmail,
                    organizations: result.organizations
                });
                
                setIsAdmin(result.isAdmin);
                console.log('🔐 isAdmin state set to:', result.isAdmin);
                
                // Force a delayed log to verify state update
                setTimeout(() => {
                    console.log('🔐 Verification: isAdmin should now be:', result.isAdmin);
                }, 100);
            } catch (error) {
                console.error('❌ Error checking admin access:', error);
                console.error('❌ Error details:', {
                    message: error.message,
                    stack: error.stack,
                    userUid: userUid,
                    userEmail: userEmail
                });
                setIsAdmin(false);
            }
        };

        checkAdmin();
    }, [
        isAuthenticated,
        isAuthorized,
        isProfileLoaded,
        user  // Only include user itself - not user?.uid or user?.email to avoid dependency issues
    ]);

    // Old user provisioning code removed - using simplified auth with OTP


    const hasNotificationPermission = useCallback(
        (notification, perms) => checkNotificationPermission(notification, perms),
        []
    );

    // Derive a stable userId for per-user notification state
    const notifUserId = useMemo(() => {
        if (!user) return null;
        const email = typeof user === 'string' ? user : user?.email;
        if (!email) return null;
        return email.replace(/[.@]/g, m => m === '.' ? '_' : '_at_');
    }, [user]);

    // Real-time listener for notifications + per-user read/dismissed state
    useEffect(() => {
        if (!isProfileLoaded || !isAuthorized) return;

        const orgId = organization?.id;
        if (!orgId) return;

        const lookbackDate = getNotificationLookbackDate();
        console.log(
            `🔔 Setting up notification listeners for org=${orgId}, role=${role || 'unknown'}, ` +
            `userId=${notifUserId}, geofenceCap=${NOTIFICATION_MAX_GEOFENCE_IN_UI}, offlineCap=${NOTIFICATION_MAX_OFFLINE_IN_UI}`
        );

        let geofenceRows = [];
        let offlineRows = [];
        let sosRows = [];

        const publishMergedNotifications = () => {
            const geofencePerm = geofenceRows.filter((n) => hasNotificationPermission(n, permissions));
            const offlinePerm = offlineRows.filter((n) => hasNotificationPermission(n, permissions));
            const sosPerm = sosRows.filter((n) => hasNotificationPermission(n, permissions));
            const filtered = buildNotificationListFromQueries(geofencePerm, offlinePerm, sosPerm);
            console.log(
                `🔔 Notifications: geofence ${geofenceRows.length} raw → ${geofencePerm.length} permitted (cap ${NOTIFICATION_MAX_GEOFENCE_IN_UI}), ` +
                `offline ${offlineRows.length} raw → ${offlinePerm.length} permitted (cap ${NOTIFICATION_MAX_OFFLINE_IN_UI}), ` +
                `${filtered.length} total in UI (role=${role || 'unknown'})`
            );
            dispatch(updateNotifications(filtered));
        };

        const geofenceQuery = query(
            collection(db, "notifications"),
            where("organizationId", "==", orgId),
            where("timestamp", ">=", Timestamp.fromDate(lookbackDate)),
            orderBy("timestamp", "desc"),
            limit(NOTIFICATION_FIRESTORE_QUERY_LIMIT)
        );

        const offlineQuery = query(
            collection(db, "notifications"),
            where("organizationId", "==", orgId),
            where("eventType", "==", "offline"),
            orderBy("timestamp", "desc"),
            limit(NOTIFICATION_OFFLINE_FIRESTORE_QUERY_LIMIT)
        );

        const unsubGeofence = onSnapshot(geofenceQuery, (snap) => {
            geofenceRows = [];
            sosRows = [];
            snap.forEach((docSnap) => {
                const row = mapNotificationDocSnap(docSnap, lookbackDate);
                if (!row) return;
                if (isOfflineCloudNotification(row)) return;
                if (isSosCloudNotification(row)) { sosRows.push(row); return; }   // LG Band SOS (same org feed)
                if (!isGeofenceCloudNotification(row)) return;
                geofenceRows.push(row);
                const data = docSnap.data();
                if (!data.deviceType && data.deviceId) {
                    console.warn(`⚠️ Notification ${docSnap.id} has deviceId but missing deviceType. Permission filtering may be inaccurate.`);
                }
            });
            publishMergedNotifications();
        }, (error) => {
            console.error("❌ Geofence notification listener error:", error);
        });

        const unsubOffline = onSnapshot(offlineQuery, (snap) => {
            offlineRows = [];
            snap.forEach((docSnap) => {
                const row = mapNotificationDocSnap(docSnap, null);
                if (!row || !isOfflineCloudNotification(row)) return;
                offlineRows.push(row);
            });
            publishMergedNotifications();
        }, (error) => {
            console.error("❌ Offline notification listener error:", error);
        });

        // Listener 2: per-user read/dismissed state (lightweight — single document)
        let unsubUserState = () => {};
        if (notifUserId) {
            const userStateRef = doc(db, "notification_state", notifUserId);
            dispatch(loadUserNotificationState(notifUserId));

            unsubUserState = onSnapshot(userStateRef, (snap) => {
                if (snap.exists()) {
                    const data = snap.data();
                    dispatch(setUserNotificationState({
                        readIds: data.readIds || [],
                        dismissedIds: data.dismissedIds || [],
                    }));
                }
            }, (error) => {
                console.error("❌ User notification state listener error:", error);
            });
        }

        return () => {
            console.log("🔔 Cleaning up notification listeners");
            unsubGeofence();
            unsubOffline();
            unsubUserState();
        };
    }, [
        dispatch,
        isProfileLoaded,
        isAuthorized,
        organization,
        permissions,
        role,
        notifUserId,
        hasNotificationPermission
    ]);

    // Client geofencing notifications (optional). Production uses scheduled_geofence_analysis + Firestore.
    useEffect(() => {
        if (!ENABLE_CLIENT_GEOFENCE_NOTIFICATIONS) {
            geofencingNotificationService.stopMonitoring();
            return;
        }

        if (!isProfileLoaded || !isAuthorized || !organization?.id) {
            return;
        }

        geofencingNotificationService.startMonitoring(dispatch, () => store.getState());

        return () => {
            geofencingNotificationService.stopMonitoring();
        };
    }, [dispatch, isProfileLoaded, isAuthorized, organization]);


    // Real-time beach geofences from Firestore `geofences` collection
    useEffect(() => {
        if (!isProfileLoaded || !isAuthorized || !organization?.id) return;
        if (!canViewBeachGeofences(permissions)) {
            dispatch(setBeachLocations([]));
            return;
        }

        const orgId = organization.id;
        const beachesRef = collection(db, 'geofences');
        const beachesQuery = query(beachesRef, where('organizationId', '==', orgId));

        console.log(`[geofences] Setting up listener for org=${orgId}`);

        const unsub = onSnapshot(
            beachesQuery,
            (snapshot) => {
                let beaches = snapshot.docs
                    .map((docSnap) => mapGeofenceDocToBeach(docSnap, orgId))
                    .filter((b) => b.polygon.length >= 3);

                if (beaches.length === 0 && snapshot.size === 0) {
                    getDocs(beachesRef).then((allSnap) => {
                        const legacy = allSnap.docs
                            .filter((d) => {
                                const oid = d.data()?.organizationId;
                                return oid == null || oid === '' || oid === orgId;
                            })
                            .map((docSnap) => mapGeofenceDocToBeach(docSnap, orgId))
                            .filter((b) => b.polygon.length >= 3);
                        if (legacy.length > 0) {
                            console.log(`[geofences] listener fallback: ${legacy.length} doc(s)`);
                            dispatch(setBeachLocations(legacy));
                        }
                    }).catch((err) => {
                        console.error('[geofences] fallback fetch error:', err);
                    });
                    return;
                }

                console.log(`[geofences] snapshot: ${beaches.length} beach geofence(s)`);
                dispatch(setBeachLocations(beaches));
            },
            (err) => {
                console.error('[geofences] Firestore listener error:', err?.code, err?.message);
            }
        );

        return () => unsub();
    }, [dispatch, isProfileLoaded, isAuthorized, organization?.id, permissions]);

    // Effect to set up real-time listeners and auto-refresh
    useEffect(() => {
        console.log('🔍 DEBUG: Data fetch useEffect triggered:');
        console.log('  - isProfileLoaded:', isProfileLoaded);
        console.log('  - isAuthorized:', isAuthorized);
        console.log('  - user:', user);
        console.log('  - organization:', organization);
        console.log('  - permissions:', permissions);
        console.log('  - role:', role);

        if (!isProfileLoaded || !isAuthorized || !organization?.id) {
            console.log('❌ Skipping data fetch - profile/org not ready');
            return;
        }

        console.log('✅ User is authorized and profile loaded - proceeding with data fetch');

        // Set up real-time listener for devices (for immediate updates)
        dispatch(setupRealtimeDeviceListener());

        // Initial fetch for all data
        console.log('🔍 DEBUG: Starting initial data fetch...');
        dispatch(fetchAllDevices());
        dispatch(fetchTowerLocations());
        dispatch(fetchBeachLocations());
        dispatch(fetchOfficeLocations());
        dispatch(fetchTritonLocations());
        dispatch(fetchAurusLocations());
        dispatch(fetchSeaHorseLocations());
        dispatch(fetchAdafruitLocations());

        // Offline device notifications are now generated by the backend scheduled function
        // (scheduled_offline_device_detection in main.py, every 5 minutes) and delivered
        // via the Firestore onSnapshot listener above. No frontend polling needed.

        return () => {
            cleanupRealtimeListener();
            dispatch(setRealtimeActive(false));
        };
    }, [dispatch, isProfileLoaded, isAuthorized, organization?.id, permissions]);

    // Real-time LG Band / Adafruit devices from Firestore (same rules as fetchAdafruitLocations).
    useEffect(() => {
        if (!isProfileLoaded || !isAuthorized) return;
        if (!canViewAdafruitLocations(permissions)) {
            dispatch(setAdafruitLocations([]));
            return undefined;
        }
        const orgId = organization?.id;
        const adafruitRef = collection(db, 'adafruit');
        // Match fetchAdafruitLocations: org-scoped query often returns 0 when docs omit
        // organizationId; that empty snapshot would overwrite a successful getDocs fallback.
        const unsubscribe = onSnapshot(
            adafruitRef,
            (snapshot) => {
                const devices = mapAdafruitSnapshotToDevices(snapshot, orgId);
                dispatch(setAdafruitLocations(devices));
                if (process.env.NODE_ENV === 'development') {
                    console.log(`[adafruit] snapshot: ${devices.length} device(s), raw docs=${snapshot.size}, orgId=${orgId || 'none'}`);
                }
            },
            (err) => {
                console.error('[adafruit] Firestore listener error:', err?.code, err?.message);
            }
        );
        return () => unsubscribe();
    }, [
        dispatch,
        isProfileLoaded,
        isAuthorized,
        organization?.id,
        permissions?.viewAdafruit,
        permissions?.viewVehicles,
    ]);

    // Real-time live-tracking trail points since user started tracking this LG Band.
    useEffect(() => {
        if (!isProfileLoaded || !isAuthorized) return undefined;
        if (!liveSessionActive || !liveSessionDeviceId || !liveSessionDocId || !liveSessionStartedAtMs) return undefined;

        const startedAtDate = new Date(liveSessionStartedAtMs);
        const readingsRef = collection(db, 'adafruit', String(liveSessionDocId), 'readings');
        const readingsQuery = query(
            readingsRef,
            where('timestamp', '>=', startedAtDate),
            orderBy('timestamp', 'asc')
        );

        const unsubscribe = onSnapshot(readingsQuery, (snapshot) => {
            snapshot.docChanges().forEach((change) => {
                if (change.type === 'removed') return;
                const data = change.doc.data();
                if (data?.gps_status === 'no_data' || data?.coordinates_valid === false) return;

                const ll = getReadingLatLng(data);
                if (!ll) return;
                const pointTimestamp = getReadingTimeMs(data, change.doc.id) ?? Date.now();
                if (pointTimestamp < liveSessionStartedAtMs) return;

                dispatch(appendLivePoint({
                    deviceId: liveSessionDeviceId,
                    lat: ll.lat,
                    lng: ll.lng,
                    timestamp: pointTimestamp,
                    status: data?.message_type || data?.message_identifier || null,
                }));
            });
        }, (error) => {
            console.error('[live-tracking] readings listener error:', error?.code, error?.message);
        });

        return () => unsubscribe();
    }, [
        dispatch,
        isProfileLoaded,
        isAuthorized,
        liveSessionActive,
        liveSessionDeviceId,
        liveSessionDocId,
        liveSessionStartedAtMs,
    ]);

    // Reset navigation flag after it's been used
    useEffect(() => {
        if (shouldNavigateToSelectedDevice) {
            const timer = setTimeout(() => {
                dispatch(setShouldNavigateToSelectedDevice(false));
            }, 1500);
            return () => clearTimeout(timer);
        }
    }, [shouldNavigateToSelectedDevice, dispatch]);

    // Shareable URL: read ?device=...&lat=...&lng=...&zoom=... from URL on first authorized load
    useEffect(() => {
        if (!isProfileLoaded || !isAuthorized) return;
        const params = new URLSearchParams(window.location.search);
        const urlLat = parseFloat(params.get('lat'));
        const urlLng = parseFloat(params.get('lng'));
        const urlDevice = params.get('device');
        if (urlLat && urlLng && !isNaN(urlLat) && !isNaN(urlLng)) {
            const tempDevice = {
                device_id: urlDevice || 'shared_location',
                displayName: urlDevice || 'Shared Location',
                last_latitude: urlLat,
                last_longitude: urlLng,
                device_type: 'shared',
                status: 'active',
            };
            dispatch(clearHistory());
            dispatch(setSelectedDevice(tempDevice));
            dispatch(setShouldNavigateToSelectedDevice(true));
            // Clean the URL params after navigating
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, [isProfileLoaded, isAuthorized, dispatch]);

    // Whenever device data refreshes, auto-resolve stale offline notifications for devices
    // that are now within the 30-minute online threshold.
    useEffect(() => {
        if (!allDevices || allDevices.length === 0) return;
        const OFFLINE_THRESHOLD_MS = 30 * 60 * 1000;
        const now = Date.now();
        const currentNotifications = store.getState().notifications.notifications;
        const offlineNotifs = currentNotifications.filter(n => n?.eventType === 'offline');
        if (offlineNotifs.length === 0) return;

        offlineNotifs.forEach(notif => {
            const device = allDevices.find(d => (d.device_id || d.id) === notif.deviceId);
            if (!device) return;
            let lastSeenMs = null;
            if (device.lastSeen?.seconds) lastSeenMs = device.lastSeen.seconds * 1000;
            else if (device.last_seen) lastSeenMs = new Date(device.last_seen).getTime();
            else if (device.timestamp?.seconds) lastSeenMs = device.timestamp.seconds * 1000;
            if (!lastSeenMs || isNaN(lastSeenMs)) return;
            if (now - lastSeenMs <= OFFLINE_THRESHOLD_MS) {
                dispatch(removeNotification(notif.id));
            }
        });
    }, [allDevices, dispatch]);

    // Handlers - Optimized with useCallback
    const handleFilterChange = useCallback((filterToAdd) => {
        console.log('🔍 handleFilterChange called with:', filterToAdd, 'type:', typeof filterToAdd);

        // Handle array of filters (multi-select)
        if (Array.isArray(filterToAdd)) {
            if (filterToAdd.length === 0 || filterToAdd.includes('all')) {
                dispatch(setActiveFilters('all'));
            } else {
                dispatch(setActiveFilters(filterToAdd));
            }
            return;
        }

        // Handle single filter (backward compatibility)
        const newFilter = filterToAdd;

        if (newFilter === 'all') {
            dispatch(setActiveFilters('all'));
            return;
        }

        let currentFilters = activeFilters.filter(f => f !== 'all');

        if (currentFilters.includes(newFilter)) {
            dispatch(removeFilter(newFilter));
        } else {
            dispatch(setActiveFilters(newFilter));
        }
    }, [dispatch, activeFilters]);

    const handleRemoveFilter = useCallback((filterToRemove) => {
        dispatch(removeFilter(filterToRemove)); // Use the correct action here as well
    }, [dispatch]);

    const handleDeviceClick = useCallback((device) => {
        console.log('🖱️ [handleDeviceClick] Device clicked in sidebar:', {
            device,
            deviceId: device.device_id,
            deviceName: device.displayName || device.name,
            hasLat: !!device.last_latitude,
            hasLng: !!device.last_longitude,
            lat: device.last_latitude,
            lng: device.last_longitude
        });
        
        // Try to find the complete device data from Redux store if coordinates are missing
        let fullDevice = device;
        if ((!device.last_latitude && !device.last_longitude) || 
            (device.last_latitude === 0 && device.last_longitude === 0)) {
            console.log('🖱️ [handleDeviceClick] Looking for device in allDevices...');
            // Look up device in allDevices to get complete data
            const foundDevice = allDevices.find(d => 
                d.device_id === device.device_id || 
                d.unique_key === device.unique_key ||
                (d.name === device.name && d.device_id === device.device_id)
            );
            if (foundDevice && foundDevice.last_latitude && foundDevice.last_longitude) {
                console.log('📦 [handleDeviceClick] Found complete device data in Redux store:', {
                    deviceId: foundDevice.device_id,
                    lat: foundDevice.last_latitude,
                    lng: foundDevice.last_longitude
                });
                fullDevice = { ...device, ...foundDevice };
            } else {
                console.warn('⚠️ [handleDeviceClick] Device not found in allDevices or missing coordinates');
            }
        }
        
        // Try multiple possible coordinate property names
        let lat = fullDevice.last_latitude || fullDevice.latitude || (fullDevice.location && fullDevice.location.latitude) || (fullDevice.coordinates && fullDevice.coordinates.lat);
        let lng = fullDevice.last_longitude || fullDevice.longitude || (fullDevice.location && fullDevice.location.longitude) || (fullDevice.coordinates && fullDevice.coordinates.lng);
        
        // Parse if strings
        if (typeof lat === 'string') lat = parseFloat(lat);
        if (typeof lng === 'string') lng = parseFloat(lng);
        
        // Ensure coordinates are properly formatted as numbers
        const normalizedDevice = {
            ...fullDevice,
            last_latitude: lat,
            last_longitude: lng,
        };
        
        console.log('📍 [handleDeviceClick] Normalized device coordinates:', { 
            lat: normalizedDevice.last_latitude, 
            lng: normalizedDevice.last_longitude,
            deviceId: normalizedDevice.device_id,
            deviceName: normalizedDevice.displayName || normalizedDevice.name,
            hasCoordinates: !!(normalizedDevice.last_latitude && normalizedDevice.last_longitude),
            latType: typeof normalizedDevice.last_latitude,
            lngType: typeof normalizedDevice.last_longitude
        });
        
        // Always set the device first
        console.log('✅ [handleDeviceClick] Dispatching setSelectedDevice');
        startLgLiveTrackingSession(dispatch, normalizedDevice);
        dispatch(setSelectedDevice(normalizedDevice));
        alertLgBandLiveTracking(dispatch, normalizedDevice);

        // Only navigate if device has valid coordinates
        if (normalizedDevice.last_latitude != null && normalizedDevice.last_longitude != null && 
            !isNaN(normalizedDevice.last_latitude) && !isNaN(normalizedDevice.last_longitude) &&
            normalizedDevice.last_latitude !== 0 && normalizedDevice.last_longitude !== 0 &&
            normalizedDevice.last_latitude >= -90 && normalizedDevice.last_latitude <= 90 &&
            normalizedDevice.last_longitude >= -180 && normalizedDevice.last_longitude <= 180) {
            console.log('✅ [handleDeviceClick] Valid coordinates, enabling navigation');
            console.log('✅ [handleDeviceClick] Dispatching setShouldNavigateToSelectedDevice(true)');
            // Use setTimeout to ensure setSelectedDevice completes first
            setTimeout(() => {
            dispatch(setShouldNavigateToSelectedDevice(true)); // Enable navigation for user clicks
                console.log('✅ [handleDeviceClick] Navigation flag set to true');
            }, 0);
        } else {
            console.warn('⚠️ [handleDeviceClick] Invalid coordinates, selecting device without navigation:', {
                lat: normalizedDevice.last_latitude,
                lng: normalizedDevice.last_longitude,
                deviceId: normalizedDevice.device_id,
                latNull: normalizedDevice.last_latitude === null,
                latUndef: normalizedDevice.last_latitude === undefined,
                latNaN: isNaN(normalizedDevice.last_latitude),
                lngNull: normalizedDevice.last_longitude === null,
                lngUndef: normalizedDevice.last_longitude === undefined,
                lngNaN: isNaN(normalizedDevice.last_longitude),
                isZero: normalizedDevice.last_latitude === 0 && normalizedDevice.last_longitude === 0
            });
            // Reset navigation flag if coordinates are invalid
            dispatch(setShouldNavigateToSelectedDevice(false));
        }
        
        const deviceInfo = document.getElementById('deviceInfo');
        if (deviceInfo) {
            deviceInfo.classList.remove('hidden');
        }
    }, [dispatch, allDevices]);

    const handleMarkerClick = useCallback((device) => {
        startLgLiveTrackingSession(dispatch, device);
        dispatch(setSelectedDevice(device));
        dispatch(setShouldNavigateToSelectedDevice(true)); // Enable navigation for user clicks
    }, [dispatch]);

    const handleCloseDeviceInfo = useCallback(() => {
        dispatch(clearHistory());
        dispatch(clearSelectedDevice());
    }, [dispatch]);

    const selectAndFocusDevice = (device) => {
        if (!device)
            return;



        console.log(`🔔 selectAndFocusDevice called with device:`, {
            device_id: device.device_id,
            name: device.name,
            displayName: device.displayName,
            last_latitude: device.last_latitude,
            last_longitude: device.last_longitude,
            hasLocation: !!(device.last_latitude && device.last_longitude)
        });

        startLgLiveTrackingSession(dispatch, device);
        dispatch(setSelectedDevice(device));
        alertLgBandLiveTracking(dispatch, device);
        dispatch(setShouldNavigateToSelectedDevice(true)); // Enable navigation for notification clicks
        const deviceInfo = document.getElementById('deviceInfo');
        if (deviceInfo) {
            deviceInfo.classList.remove('hidden');
        }
        console.log(`🔔 Focusing on device: ${device.displayName || device.device_id
            }`);
    };

    // Locate an LG Band (by map id "lg_band_<docId>") from the live adafruit snapshot and fly to it.
    const locateSosDevice = useCallback((deviceId) => {
        const row = (adafruitLocations || []).find((r) => r.device_id === deviceId);
        if (!row) {
            console.warn('🆘 locateSosDevice: LG Band not in adafruitLocations:', deviceId);
            return;
        }
        const device = toLgBandMapDevice(row);
        const anchor = getSosAnchorLatLng(device);   // SOS point if an incident is open, else live
        selectAndFocusDevice(anchor ? { ...device, last_latitude: anchor.lat, last_longitude: anchor.lng } : device);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adafruitLocations]);
    locateSosDeviceRef.current = locateSosDevice;

    // Closing an SOS is an Atlas action: mark the incident closed in Firestore (all operators), then hide the banner.
    const closeSosFromAtlas = useCallback((alertId, alertData) => {
        const a = alertData || alerts.find((x) => x.id === alertId);
        const docId = a?.firestoreDocId
            || (adafruitLocations || []).find((r) => r.device_id === a?.deviceId)?.adafruit_firestore_doc_id;
        if (docId) {
            closeSosIncident(docId, user?.email || user?.uid || null)
                .catch((e) => console.error('🆘 closeSosIncident failed:', e));
        }
        dispatch(dismissAlert(alertId));
    }, [alerts, adafruitLocations, user, dispatch]);

    const handleNotificationClick = async (deviceId, notificationData = null) => {
        if (typeof deviceId === 'string' && deviceId.startsWith('lg_band_')) {
            locateSosDevice(deviceId);
            return;
        }
        console.log(`🔔 Notification click handler called with deviceId: "${deviceId}"`, notificationData);
        console.log(`🔍 Current allDevices count: ${allDevices.length
            }`);

        const KNOWN_DEVICE_PREFIXES = [
            'mobile_',
            'aurus_',
            'triton_',
            'vehicle_',
            'ebike_'
        ];
        let normalizedDeviceId = deviceId;

        // Sanity check for incoming deviceId
        if (typeof deviceId !== 'string' || deviceId.length === 0) {
            console.error("Invalid deviceId received from notification:", deviceId);
            return;
        }

        // Check for and strip known prefixes
        for (const prefix of KNOWN_DEVICE_PREFIXES) {
            if (normalizedDeviceId.startsWith(prefix)) {
                normalizedDeviceId = normalizedDeviceId.substring(prefix.length);
                console.log(`Normalized deviceId from "${deviceId}" to "${normalizedDeviceId}"`);
                break;
            }
        }

        // Find the device in the current state
        let device = allDevices.find(d => d.device_id === normalizedDeviceId);

        if (device) {
            selectAndFocusDevice(device);
            return;
        }

        // If not found, try to refresh data once
        console.warn(`Device ${normalizedDeviceId} not found in cache. Refreshing device list...`);

        try {
            await dispatch(fetchAllDevices()).unwrap();

            // Get the fresh devices from Redux state after refresh
            const state = store.getState();
            const freshAllDevices = state.devices.allDevices;

            console.log(`🔍 After refresh - freshAllDevices count: ${freshAllDevices.length
                }`);
            console.log(`🔍 Looking for device with ID: "${normalizedDeviceId}"`);

            // Debug: Show some sample device IDs
            if (freshAllDevices.length > 0) {
                console.log(`🔍 Sample device IDs:`, freshAllDevices.slice(0, 5).map(d => d.device_id));
            }

            const freshDevice = freshAllDevices.find(d => d.device_id === normalizedDeviceId);

            if (freshDevice) {
                selectAndFocusDevice(freshDevice);
            } else { // If still not found, try to find by the original deviceId (with prefix)
                const deviceWithPrefix = freshAllDevices.find(d => d.device_id === deviceId);
                if (deviceWithPrefix) {
                    selectAndFocusDevice(deviceWithPrefix);
                } else { // If device is not found at all, try to find the geofence and center on it
                    console.warn(`Device ${deviceId} not found. Looking for geofence location...`);

                    // Try to find the geofence using notification data first, then fallback to deviceId search
                    let geofence = null;
                    if (notificationData && notificationData.geofenceName) {
                        geofence = beachLocations.find(beach => beach.name && beach.name.toLowerCase().includes(notificationData.geofenceName.toLowerCase()));
                    }

                    // If not found with geofence name, try with deviceId
                    if (!geofence) {
                        geofence = beachLocations.find(beach => beach.name && beach.name.toLowerCase().includes(deviceId.toLowerCase()));
                    }

                    if (geofence && geofence.polygon && geofence.polygon.length > 0) { // Calculate center of the geofence polygon
                        const centerLat = geofence.polygon.reduce((sum, point) => sum + point.lat, 0) / geofence.polygon.length;
                        const centerLng = geofence.polygon.reduce((sum, point) => sum + point.lng, 0) / geofence.polygon.length;

                        // Create a temporary device object to center the map
                        const tempDevice = {
                            device_id: deviceId,
                            displayName: deviceId,
                            last_latitude: centerLat,
                            last_longitude: centerLng,
                            device_type: 'geofence',
                            status: 'active'
                        };

                        selectAndFocusDevice(tempDevice);
                        const eventType = notificationData?.eventType || 'event';
                        const deviceName = notificationData?.deviceName || deviceId;
                        dispatch(addAlert(`Centered on geofence: ${geofence.name
                            } (${deviceName} ${eventType})`));
                    } else { // If no geofence found, show a helpful message
                        dispatch(addAlert(`Device ${deviceId} not found. This might be a geofence entry/exit notification.`));
                    }
                }
            }
        } catch (error) {
            console.error('Error refreshing devices:', error);
            dispatch(addAlert('Error refreshing device data. Please try again.'));
        }
    };

    const handleMarkAsRead = useCallback(() => {
        dispatch(markNotificationsAsRead(notifUserId));
    }, [dispatch, notifUserId]);

    const handleClearAll = useCallback(() => {
        dispatch(clearAllNotifications(notifUserId));
    }, [dispatch, notifUserId]);

    // Simplified login handler
    const handleGoogleSignIn = useCallback(async () => {
        try {
            console.log('🔄 Starting simplified Google sign-in...');
            await dispatch(signInWithGoogle()).unwrap();
            console.log('✅ Simplified Google sign-in successful');
        } catch (error) {
            console.error('❌ Simplified Google sign-in failed:', error);
            // We don't need to dispatch an alert here anymore because the login screen will show the error.
            // dispatch(addAlert({ id: Date.now(), type: 'error', message: `Sign-in failed: ${error}`, timestamp: new Date().toISOString() }));
        }
    }, [dispatch]);

    // Simplified logout handler
    const handleLogout = useCallback(async () => {
        try {
            console.log('🔄 Starting simplified sign-out...');
            await dispatch(signOut()).unwrap();
            console.log('✅ Simplified sign-out successful');
        } catch (error) {
            console.error('❌ Simplified sign-out failed:', error);
        }
    }, [dispatch]);

    // OTP verification handlers
    const handleOTPVerificationSuccess = useCallback((user) => {
        console.log('✅ OTP verification successful for:', user.email);
        dispatch(setOTPVerificationCompleted(true));
    }, [dispatch]);

    const handleOTPBack = useCallback(() => {
        console.log('🔄 Going back to login from OTP verification');
        dispatch(setOTPVerificationRequired(false));
        dispatch(clearOTPError());
        // Sign out to restart the flow
        dispatch(signOut());
    }, [dispatch]);

    const handleBeachGeofenceClick = useCallback((beachData, devicesInBeach) => {
        dispatch(setSelectedBeachGeofence({
            beach: beachData,
            devices: normalizeDevicesForRedux(devicesInBeach),
        }));
    }, [dispatch]);

    const closeBeachPanel = () => {
        dispatch(clearSelectedBeachGeofence());
    };

    const capitalizeFirst = (str) => {
        if (!str)
            return 'N/A';



        return str.charAt(0).toUpperCase() + str.slice(1);
    };

    const showTowers = (permissions?.viewTowers === true && (activeFilters.includes('all') || activeFilters.includes('towers')));
    const showTriton = (permissions?.viewTriton === true && (activeFilters.includes('all') || activeFilters.includes('triton')));
    const showAurus = (permissions?.viewAurus === true && (activeFilters.includes('all') || activeFilters.includes('aurus')));


    if (!isAuthReady) { // Optionally return a loading spinner here
        return (
            <div className="flex justify-center items-center min-h-screen bg-white">
                <div className="text-center p-10">
                    <div className="w-12 h-12 border-4 border-gray-200 border-t-[#004aad] rounded-full animate-spin mx-auto mb-6"></div>
                    <h2 className="text-[#004aad] text-2xl font-semibold">Loading Authentication...</h2>
                </div>
            </div>
        );
    }

    // Use simplified Google login component
    const LoginComponent = GoogleLogin;

    // Debug logging for render state
    console.log('🔍 Render state:', {
        isAuthenticated,
        otpVerificationRequired,
        isAuthorized,
        isProfileLoaded,
        user: user?.email,
        userUid: user?.uid,
        userHasEmail: !!user?.email,
        userObject: user, // Full user object for debugging
        isAdmin: isAdmin
    });

    // Force reload to clear cache
    return (<TempAccessHandler> {!isAuthenticated ? (
        <LoginComponent onSignIn={handleGoogleSignIn} error={authError === 'No stored session found' ? null : authError} loading={authLoading} loadingPhase={authLoadingPhase} />
    ) : otpVerificationRequired ? (
        <OTPVerification
            user={user}
            onVerificationSuccess={handleOTPVerificationSuccess}
            onBack={handleOTPBack}
        />
    ) : !isAuthorized && !isProfileLoaded ? (
        // Show loading state while profile is being loaded after OTP verification
        <div className="flex justify-center items-center min-h-screen bg-white p-4">
            <div className="text-center p-10 bg-white rounded-2xl shadow-2xl max-w-sm w-full">
                <div className="w-12 h-12 border-4 border-gray-200 border-t-[#004aad] rounded-full animate-spin mx-auto mb-6"></div>
                <h2 className="text-[#004aad] text-2xl font-semibold mb-3">Setting up your access...</h2>
                <p className="text-gray-600 text-base leading-relaxed">Please wait while we configure your permissions.</p>
            </div>
        </div>
    ) : !isAuthorized ? (
        <div className="flex items-center justify-center min-h-screen bg-white p-5">
            <div className="bg-white rounded-2xl p-5 text-center shadow-2xl max-w-lg w-full md:p-10 md:m-5">
                <img src="/Atlas_big_bar.png" alt="Atlas Logo" className="h-16 w-auto mb-8 mx-auto md:h-20" />
                <h1 className="text-red-600 text-2xl mb-5 font-bold md:text-3xl">Access Denied</h1>
                <p className="text-gray-600 text-base mb-4 leading-relaxed md:text-lg">You are not authorized to access this system.</p>
                <p className="text-gray-600 text-base mb-4 leading-relaxed md:text-lg">Please contact the admin to request access.</p>
                <button className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white border-none py-3 px-8 rounded-lg text-base font-semibold cursor-pointer mt-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_20px_rgba(102,126,234,0.3)]" onClick={handleLogout}>
                    Sign Out
                </button>
            </div>
        </div>
    ) : (
        <>
            <MainLayout
                user={user}
                handleLogout={handleLogout}
                handleNotificationClick={handleNotificationClick}
                locateSosDevice={locateSosDevice}
                closeSosFromAtlas={closeSosFromAtlas}
                isBackendOnline={isBackendOnline}
                lastUpdateTime={lastUpdateTime}
                isMobile={isMobile}
                isSidebarOpen={isSidebarOpen}
                toggleSidebar={() => dispatch(toggleSidebar())}
                activeFilters={activeFilters}
                handleFilterChange={handleFilterChange}
                filterOptions={filterOptions}
                handleRemoveFilter={handleRemoveFilter}
                showNameplates={showNameplates}
                toggleNameplates={() => dispatch(toggleNameplates())}
                showGeofences={showGeofences}
                toggleGeofences={() => dispatch(toggleGeofences())}
                isAdmin={isAdmin}
                openAdminPanel={() => dispatch(openAdminPanel())}
                filteredDevices={filteredDevices}
                selectedDevice={selectedDevice}
                handleDeviceClick={handleDeviceClick}
                handleCloseDeviceInfo={handleCloseDeviceInfo}
                // MapComponent props
                devices={filteredDevices}
                towerLocations={towerLocations}
                beachLocations={beachLocations}
                officeLocations={officeLocations}
                tritonLocations={tritonLocations}
                aurusLocations={aurusLocations}
                seaHorseLocations={seaHorseLocations}
                showTowers={showTowers}
                showTriton={showTriton}
                showAurus={showAurus}
                showSeaHorse={true}
                onMarkerClick={handleMarkerClick}
                // selectedDevice is already passed above
                // showNameplates is already passed above
                // showGeofences is already passed above
                onBeachGeofenceClick={handleBeachGeofenceClick}
                shouldNavigateToSelectedDevice={shouldNavigateToSelectedDevice}
                permissions={permissions}
                // Other props for layout
                alerts={alerts}
                dismissAlert={(id) => dispatch(dismissAlert(id))}
                dismissAllAlerts={() => dispatch(dismissAllAlerts())}
                selectedBeachGeofence={selectedBeachGeofence}
                closeBeachPanel={closeBeachPanel}
                isAdminPanelOpen={isAdminPanelOpen}
                closeAdminPanel={() => dispatch(closeAdminPanel())}
                selectedCity={selectedCity}
                setSelectedCity={(city) => dispatch(setSelectedCity(city))}
                selectedState={selectedState}
                setSelectedState={(state) => dispatch(setSelectedState(state))}
                selectedCountry={selectedCountry}
                setSelectedCountry={(country) => dispatch(setSelectedCountry(country))}
                setIsBoundaryLoading={setIsBoundaryLoading}
                isBoundaryLoading={isBoundaryLoading}
                activeFilters={activeFilters}
            />
        </>
    )}
    </TempAccessHandler>
    );
}

export default AppContentRedux;
